import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@boltcrm/module-sdk/web';
import { DEFAULT_INTERFACE_PREFS, type InterfacePrefs } from '@boltcrm/platform/shared';
import { useActiveCompany } from './session';

const key = ['interface-prefs'] as const;

/** The user's interface preferences (`/api/v1/me/preferences`); the router's home redirect reads it too. */
export const interfacePrefsQuery = {
  queryKey: key,
  queryFn: () => apiFetch<{ data: InterfacePrefs }>('/api/v1/me/preferences').then((r) => r.data),
  staleTime: 5 * 60_000,
};

/** The preferences query itself, for callers that must tell "loaded" from the defaults (the theme sync). */
export const useInterfacePrefsQuery = () => useQuery(interfacePrefsQuery);

/** The user's interface preferences, with defaults while loading. */
export function useInterfacePrefs(): InterfacePrefs {
  const { data } = useInterfacePrefsQuery();
  return data ?? DEFAULT_INTERFACE_PREFS;
}

/**
 * Interface preferences → Navigation → "Keyboard shortcuts" (ADR-143): single-key shortcuts (`/`, `.` `+`, `[` `]`,
 * J / K) listen only while it's on. On while loading, its default.
 */
export const useKeyboardShortcuts = (): boolean => useInterfacePrefs().keyboardShortcuts;

/**
 * The currency new values start in (Personal preferences → Default currency, PP5, ADR-450): the user's own choice, else
 * the company's default currency.
 */
export function useDefaultCurrency(): string {
  const mine = useInterfacePrefs().defaultCurrency;
  const company = useActiveCompany()?.defaultCurrency;
  return mine ?? company ?? 'USD';
}

/** The window event the shell's won-deal celebration listens for (PP4, ADR-449). */
export const WON_CELEBRATION_EVENT = 'boltcrm:deal-won';

/** Asks the shell to celebrate a won deal; it shows the animation while the user's preference is on. */
export function celebrateWonDeal(): void {
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(WON_CELEBRATION_EVENT));
}

/** Saves a preference optimistically; on failure the previous value comes back (and the error reaches `onError`). */
export function useUpdateInterfacePrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<InterfacePrefs>) =>
      apiFetch<{ data: InterfacePrefs }>('/api/v1/me/preferences', { method: 'PATCH', body: patch }).then(
        (r) => r.data,
      ),
    onMutate: async (patch) => {
      await qc.cancelQueries({ queryKey: key });
      const previous = qc.getQueryData<InterfacePrefs>(key);
      qc.setQueryData<InterfacePrefs>(key, { ...DEFAULT_INTERFACE_PREFS, ...previous, ...patch });
      return { previous };
    },
    onError: (_e, _patch, context) => qc.setQueryData(key, context?.previous),
    onSuccess: (data) => qc.setQueryData(key, data),
  });
}
